import { CONNECTING } from './connectingContent.ts';
import { ASSESSING } from './assessingContent.ts';
import { REPORTING } from './reportingContent.ts';
import { OPERATING } from './operatingContent.ts';

/**
 * The console's own documentation, as data.
 *
 * The same shape as `legal/documents.ts` and for the same reason: the index,
 * the anchors and the section numbering are **derived** from these arrays
 * rather than maintained beside them, so a new section cannot be added to one
 * and forgotten in the other.
 *
 * Prose lives in the four sibling content modules, one per group. This file
 * holds the shape and the registry, which keeps every file inside the size
 * band and means a wording change never touches the structure.
 *
 * **Everything here describes what the product does today.** A page that
 * describes an intention is worse than no page: an operator who is told the
 * console can do something it cannot will conclude they are holding it wrong.
 * Where a capability is partial — thresholds are readable in the console and
 * writable only through the API — the article says so in those words.
 */

export interface DocSection {
  /** The anchor. Stable across rewordings of the heading. */
  readonly id: string;
  readonly heading: string;
  readonly paragraphs: readonly string[];
  /**
   * Commands to be typed exactly, one block each, rendered after the
   * paragraphs and before the bullets. Kept apart from the prose because a
   * command inside a sentence gets its quotes curled and its line wrapped on
   * the way to somebody's terminal.
   */
  readonly code?: readonly string[];
  readonly bullets?: readonly string[];
}

export interface DocArticle {
  readonly id: string;
  readonly title: string;
  /** One sentence, shown in the index beside the title. */
  readonly summary: string;
  readonly sections: readonly DocSection[];
}

export interface DocGroup {
  readonly id: string;
  readonly label: string;
  readonly articles: readonly DocArticle[];
}

export const DOC_GROUPS: readonly DocGroup[] = [
  { id: 'connecting', label: 'Connecting clients', articles: CONNECTING },
  { id: 'assessing', label: 'Assessing', articles: ASSESSING },
  { id: 'reporting', label: 'Reporting', articles: REPORTING },
  { id: 'operating', label: 'Running the deployment', articles: OPERATING },
];

export const DOC_ARTICLES: readonly DocArticle[] = DOC_GROUPS.flatMap((group) => group.articles);

export const articleFor = (id: string): DocArticle | null =>
  DOC_ARTICLES.find((article) => article.id === id) ?? null;

/** The article a reader lands on. The first one, and it is the tour. */
export const FIRST_ARTICLE = DOC_ARTICLES[0] as DocArticle;
